import React, { useEffect } from "react";
import AccessDenied from "./AccessDenied";
import { Loading } from "./Loader";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { fetchMe, selectAuth, selectIsAuthenticated } from "../features/auth/authSlice";
import { getLandingRoute } from "../app/navigation";

/**
 * Общий гард роутов: грузит пользователя и пускает, если `isAllowed(user)`.
 * Конкретные правила — в `RequireGroups` (по группам) и `RequirePermission` (по матрице прав).
 *
 * Нет доступа:
 * - на `/` или при явном `forbiddenTo` — тихий редирект на стартовую страницу роли: на «/»
 *   попадают сразу после входа, и это не ошибка пользователя;
 * - в остальных случаях — экран «Нет доступа» с названием раздела. Молчаливый редирект
 *   выглядел как «ссылка не работает».
 */
export function RequireAccess({ isAllowed, children, forbiddenTo = null }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthed = useSelector(selectIsAuthenticated);
  const auth = useSelector(selectAuth);

  const allowed = Boolean(auth.user && isAllowed(auth.user));
  const redirectWhenForbidden = Boolean(forbiddenTo) || location.pathname === "/";

  useEffect(() => {
    if (isAuthed && !auth.user && auth.status === "idle") {
      dispatch(fetchMe());
    }
  }, [auth.status, auth.user, dispatch, isAuthed]);

  useEffect(() => {
    if (!auth.user) return;
    if (auth.status === "failed") return;
    if (!allowed && redirectWhenForbidden) {
      // Без явного forbiddenTo возвращаем на стартовую страницу роли: жёсткий редирект на «/»
      // зациклился бы для ролей, которым сама «/» недоступна (например, ревизор).
      const target = forbiddenTo || getLandingRoute(auth.user);
      navigate(target, { replace: true, state: { reason: "forbidden" } });
    }
  }, [allowed, auth.user, auth.status, forbiddenTo, navigate, redirectWhenForbidden]);

  if (!isAuthed) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // IMPORTANT: on hard reload auth.status starts as "idle" and fetchMe runs in useEffect.
  // During that initial render auth.user is still null, so we must NOT treat it as "forbidden".
  if (!auth.user && (auth.status === "idle" || auth.status === "loading")) {
    return <Loading text="Проверка доступа…" />;
  }

  if (!auth.user && auth.status === "failed") {
    return <Navigate to="/auth/login" state={{ from: location, reason: "failed_to_load_user" }} replace />;
  }

  if (!allowed && !redirectWhenForbidden) {
    return <AccessDenied homeTo={getLandingRoute(auth.user)} />;
  }

  if (!allowed) {
    return (
      <div style={{ padding: 16, color: "var(--c-text-muted)", fontSize: 15 }}>
        Перенаправление…
      </div>
    );
  }

  return children;
}
